import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import type {
  EmployeeSummary,
  PageResponse,
  ProjectMemberView,
  ProjectRole,
} from "../../shared/types/api";
import { PROJECT_ROLE_LABELS } from "./projectVocabulary";

const today = () => new Date().toISOString().slice(0, 10);

const ROLES = Object.keys(PROJECT_ROLE_LABELS) as ProjectRole[];

function describe(error: unknown): string {
  if (error instanceof ApiError && error.code === "MEMBERSHIP_OVERLAP") {
    return (
      "That employee is already on this project over an overlapping period. " +
      "End the existing involvement first, or choose a later start date."
    );
  }
  return error instanceof ApiError ? error.message : "Something went wrong. Try again.";
}

/**
 * Adds someone to a project from a date (§13).
 *
 * <p>No end-date field, deliberately. Involvement is opened here and closed through its own
 * action; offering both together invites people to express a change as one edit, which is exactly
 * the overwrite the effective-dated model exists to prevent.
 */
export function AddProjectMemberDrawer({
  projectId,
  open,
  onClose,
  onSaved,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [employeeId, setEmployeeId] = useState("");
  const [projectRole, setProjectRole] = useState<ProjectRole>("DEVELOPER");
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setEmployeeId("");
      setProjectRole("DEVELOPER");
      setEffectiveFrom(today());
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/projects/${projectId}/members`, {
        employeeId,
        projectRole,
        effectiveFrom,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add project member"
      subtitle="Their involvement starts on the date you choose."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={employeeId === "" || effectiveFrom === ""}
          >
            {saving ? "Adding…" : "Add member"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Employee" required>
          <Select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
            <option value="">Choose an employee</option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName} — {employee.designation}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Project role"
          required
          hint="What they do on this project, which can differ from their designation."
        >
          <Select
            value={projectRole}
            onChange={(event) => setProjectRole(event.target.value as ProjectRole)}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {PROJECT_ROLE_LABELS[role]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Involved from" required>
          <Input
            type="date"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}

/**
 * Ends an involvement on a date.
 *
 * <p>Phrased as ending rather than removing, because that is what happens: the record stays, and
 * every report covering the period they were here still names them.
 */
export function CloseProjectMemberDrawer({
  projectId,
  member,
  onClose,
  onSaved,
}: {
  projectId: string;
  member: ProjectMemberView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [effectiveTo, setEffectiveTo] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) {
      setEffectiveTo(today());
      setError(null);
    }
  }, [member]);

  const submit = async () => {
    if (!member) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(
        `/api/v1/projects/${projectId}/members/${member.membershipId}/close`,
        { effectiveTo },
      );
      onSaved();
      onClose();
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setSaving(false);
    }
  };

  // The period is half-open, so the end date is the first day they are no longer on the project.
  // Saying so plainly avoids the off-by-one nobody notices until a capacity report is wrong.
  const tooEarly = member !== null && effectiveTo <= member.effectiveFrom;

  return (
    <Drawer
      open={Boolean(member)}
      onClose={onClose}
      title="End involvement"
      subtitle={member?.fullName}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={tooEarly}>
            {saving ? "Saving…" : "End involvement"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          Nothing is deleted. {member?.fullName} stays on this project's record from{" "}
          {member?.effectiveFrom} up to the date below, so worklogs and reports covering that
          period remain correct.
        </InlineAlert>

        <Field
          label="Last day on the project is the day before"
          required
          hint="Involvement ends at the start of this date."
        >
          <Input
            type="date"
            value={effectiveTo}
            min={member?.effectiveFrom}
            onChange={(event) => setEffectiveTo(event.target.value)}
          />
        </Field>

        {tooEarly && (
          <InlineAlert tone="warning">
            The end date has to be after {member?.effectiveFrom}, when the involvement started.
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}
